import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  // saveDocument,
  deleteDocument,
  viewDocument,
  saveDocumentList,
  getS3SignUploadUrl,
  uploadDirectlyToS3,
} from '../../../services/uploadDocuments';

export const QUERY_KEYS = {
  documents: (folderName) => ['documents', folderName],
};

export const useSaveDocument = (folderName, { onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, isExisting, listId, onUploadProgress }) => {
      // Step 1 — ask the backend for a pre-signed S3 POST policy.
      const signPayload = {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type,
        folderName,
      };

      if (isExisting) {
        signPayload.listId = listId;
      }

      const { documentId, uploadUrl, uploadFields } = await getS3SignUploadUrl(signPayload);

      // Step 2 — upload the bytes directly to S3 using the signed fields.
      // S3's POST policy requires the "file" field to be the LAST field
      // in the multipart body, after all the policy/signature fields.
      const formData = new FormData();
      Object.entries(uploadFields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      await uploadDirectlyToS3(uploadUrl, formData, onUploadProgress);

      // S3 replies 204 No Content on success, so there's nothing to read back —
      // just carry forward the documentId we already got from the sign step.
      return { documentId };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documents(folderName) });
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      const fallbackMessage =
        'The uploaded document does not contain the required format or information needed for analysis. Please upload a valid document and try again.';

      alert(error?.detail || error?.message || fallbackMessage);
      onError?.(error, variables);
    },
  });
};

export const useDeleteDocument = (listId, { onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId }) => deleteDocument({ documentId, listId }),

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documents(listId) });
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);
    },
  });
};

export const useViewDocument = (listId, { onSuccess, onError } = {}) => {
  return useMutation({
    mutationFn: ({ documentId }) => viewDocument({ documentId, listId }),

    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);
    },
  });
};

export const useSaveDocumentList = ({ onSuccess, onError } = {}) => {
  return useMutation({
    mutationFn: ({ title, folderName, pdfArray, description }) =>
      saveDocumentList({ title, folderName, pdfArray, description }),
    onSuccess: (data, variables) => onSuccess?.(data, variables),
    onError: (error, variables) => onError?.(error, variables),
  });
};
